import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { KeyRound, CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleRequest = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return toast.error('Enter your email');
    setLoading(true);
    try {
      await axios.post(`${API}/api/users/password-reset`, {
        email: cleanEmail,
        frontend_origin: window.location.origin,
      });
      setEmailSent(true);
      toast.success('If this email exists, a reset link has been sent.');
    } catch {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/api/users/password-reset/complete`, { token, password });
      // Clear sensitive state immediately so it cannot leak via React DevTools
      setPassword('');
      setConfirm('');
      setDone(true);
      toast.success('Password updated! Your new password is now active.');
      // Auto-redirect to login after a short pause so the user reads the confirmation
      setTimeout(() => {
        navigate('/login?reset=success', { replace: true });
      }, 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired token');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <Card className="w-full max-w-sm bg-zinc-900 border-zinc-800" data-testid="reset-success">
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto" />
            <p className="text-zinc-100 font-medium">Password updated successfully</p>
            <p className="text-xs text-zinc-400 leading-relaxed">
              Your new password is now active.<br />
              Sign in below with your <strong>new</strong> password.
            </p>
            <Button
              onClick={() => navigate('/login?reset=success', { replace: true })}
              className="w-full"
              data-testid="reset-go-login"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (token) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <Card className="w-full max-w-sm bg-zinc-900 border-zinc-800" data-testid="reset-password-form">
          <CardHeader>
            <CardTitle className="text-center text-zinc-100 flex items-center justify-center gap-2">
              <KeyRound className="w-5 h-5" /> Set New Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Hidden email field improves password-manager association to the right account */}
            <input
              type="email"
              name="username"
              autoComplete="username"
              defaultValue=""
              tabIndex={-1}
              aria-hidden="true"
              style={{ display: 'none' }}
            />
            <Input
              type="password"
              placeholder="New password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              className="bg-zinc-950 border-zinc-800"
              data-testid="reset-new-password"
            />
            <Input
              type="password"
              placeholder="Confirm password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="bg-zinc-950 border-zinc-800"
              data-testid="reset-confirm-password"
            />
            <Button onClick={handleReset} disabled={loading} className="w-full" data-testid="reset-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <Card className="w-full max-w-sm bg-zinc-900 border-zinc-800" data-testid="reset-request-form">
        <CardHeader>
          <CardTitle className="text-center text-zinc-100">Password Recovery</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {emailSent ? (
            <div className="text-center space-y-3" data-testid="reset-email-sent">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto" />
              <p className="text-sm text-zinc-300">If an account exists for this email, a reset link has been sent.</p>
              <Button variant="outline" onClick={() => navigate('/login')} className="w-full">Back to Login</Button>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500">Enter your email address and we'll send you a link to reset your password.</p>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="username"
                className="bg-zinc-950 border-zinc-800"
                data-testid="reset-email-input"
              />
              <Button onClick={handleRequest} disabled={loading} className="w-full" data-testid="reset-request-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
              </Button>
              <Button variant="ghost" onClick={() => navigate('/login')} className="w-full text-zinc-500">Back to Login</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
